import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../utils/formatDate';
import AppContext from '../contexts/AppContext';
import { canDisplayField } from '../utils/permissionUtils';

function Item({ label, value, icon }) {
  if (value === undefined || value === null || value === '') return null;

  return (
    <div className="flex items-start gap-3 p-3 rounded-lg border border-gray-100">
      <div className="text-gray-400 mt-0.5">
        {icon}
      </div>
      <div>
        <div className="text-xs uppercase tracking-wide text-gray-500">{label}</div>
        <div className="text-sm text-gray-900">{value}</div>
      </div>
    </div>
  );
}

function IssueInfo({ issue, getPriorityPill }) {
  const { t } = useTranslation();
  const { fields, mappedFields } = issue;
  const { serviceDesk, currentUser } = useContext(AppContext);
  const { fieldsAllowedToDisplay } = serviceDesk?.config;

  const priority = fields.priority?.name ? (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getPriorityPill(fields.priority.name)}`}>
      {fields.priority.name}
    </span>
  ) : null;

  const requestType = mappedFields.requestType?.requestType?.name || '-';
  const participants = Array.isArray(mappedFields.requestParticipants) ? mappedFields.requestParticipants : [];
  const technician = participants.map((p) => p?.displayName).filter(Boolean).join(', ') || null;
  const resolution = fields.resolution || null;
  const resolutionDate = fields.resolutiondate ? formatDateTime(fields.resolutiondate) : null;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
      <div className="flex items-center justify-between mb-4 border-b border-gray-100 pb-2">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900">{t('Information')}</h3>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Item label={t('Reporter')} value={issue?.fields?.reporter?.displayName || '-'} icon={
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A9 9 0 1118.88 17.804M15 11a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
        } />
        <Item label={t('Technician')} value={technician} icon={
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.42 15.17L17.25 21A2.652 2.652 0 0021 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 11-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 004.486-6.336l-3.276 3.277a3.004 3.004 0 01-2.25-2.25l3.276-3.276a4.5 4.5 0 00-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085m-1.745 1.437L5.909 7.5H4.5L2.25 3.75l1.5-1.5L7.5 4.5v1.409l4.26 4.26m-1.745 1.437l1.745-1.437m6.615 8.206L15.75 15.75M4.867 19.125h.008v.008h-.008v-.008z"/></svg>
        } />
        <Item label={t('Request type')} value={requestType} icon={
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V7a2 2 0 012-2h5l2 2h5a2 2 0 012 2v10a2 2 0 01-2 2z"/></svg>
        } />
        <Item label={t('Created')} value={fields.created ? formatDateTime(fields.created) : '-'} icon={
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
        } />
        <Item label={t('Updated')} value={fields.updated ? formatDateTime(fields.updated) : '-'} icon={
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582a10.97 10.97 0 0115.356 0H20V4M4 20v-5h.582a10.97 10.97 0 0115.356 0H20v5"/></svg>
        } />
        {fields.priority && canDisplayField(fieldsAllowedToDisplay, 'priorityField', currentUser.role) && (
          <Item label={t('Priority')} value={priority} icon={
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9l3 3-3 3m0-6l-3 3 3 3"/></svg>
          } />
        )}
        {resolution && (
          <Item label={t('Resolution')} value={resolution?.name || '-'} icon={
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/></svg>
          } />
        )}
        {resolutionDate && (
          <Item label={t('Resolution date')} value={resolutionDate} icon={
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2h-3.5a2 2 0 01-1.6-.8l-.3-.4a2 2 0 00-1.6-.8H9a2 2 0 00-1.6.8l-.3.4a2 2 0 01-1.6.8H2a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
          } />
        )}
      </div>
    </div>
  );
}

export default IssueInfo;


